import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useId,
  useMemo,
} from 'react';
import {
  Badge,
  Box,
  Flex,
  HStack,
  InputGroup,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { RiftboundButton } from './ui/button';
import { RiftboundIconButton } from './ui/icon-button';
import { MdClose, MdHistory, MdFilterList, MdSearch } from 'react-icons/md';
import { getApiBaseUrl } from '../constants/environment';
import { stripTrailingPrintSuffix } from '../utils/cardPrintSuffix';
import { DomainIcon, getRarityIcon } from './icons/FacetIcons';
import { RiftboundInput } from './ui/input';
import { dropdownBg, raisedShadow } from '../theme/themeColor';
import {
  CardSearchSuggestionRow,
  SearchSuggestionSectionLabel,
  type CardSuggestion,
} from './CardSearchSuggestionRow';

// ─── Types ───────────────────────────────────────────────────────────────────

export type { CardSuggestion };

export type FacetCategory =
  | 'keyword'
  | 'cardType'
  | 'supertype'
  | 'region'
  | 'subtype'
  | 'rarity'
  | 'domain'
  | 'artist';

/** Categories that drive the tab bar inside the dropdown. */
type TabId = 'all' | 'cards' | FacetCategory;

interface TabDef {
  id: TabId;
  label: string;
}

const TABS: TabDef[] = [
  { id: 'all', label: 'All' },
  { id: 'cards', label: 'Cards' },
  { id: 'keyword', label: 'Keywords' },
  { id: 'cardType', label: 'Types' },
  { id: 'supertype', label: 'Supertypes' },
  { id: 'subtype', label: 'Subtypes' },
  { id: 'region', label: 'Regions' },
  { id: 'domain', label: 'Domains' },
  { id: 'rarity', label: 'Rarities' },
  { id: 'artist', label: 'Artists' },
];

const FACET_LABEL: Record<FacetCategory, string> = {
  keyword: 'Keyword',
  cardType: 'Type',
  supertype: 'Supertype',
  region: 'Region',
  subtype: 'Subtype',
  rarity: 'Rarity',
  domain: 'Domain',
  artist: 'Artist',
};

/** Hard-coded domain values — these don't come from the facets endpoint. */
const DOMAINS = ['Body', 'Calm', 'Chaos', 'Fury', 'Mind', 'Order'];

/** Maximum items shown per facet category in the "All" tab. */
const ALL_TAB_FACET_LIMIT = 3;
/** Maximum card-name results shown in the "All" tab. */
const ALL_TAB_CARDS_LIMIT = 4;
/** Maximum items in any single-category tab. */
const SINGLE_TAB_LIMIT = 30;

interface FacetsInput {
  keywords?: string[];
  cardTypes?: string[];
  supertypes?: string[];
  regions?: string[];
  subtypes?: string[];
  rarities?: string[];
  /** Optional override — defaults to the hard-coded DOMAINS list. */
  domains?: string[];
  artists?: string[];
}

// Internal flat row used to drive keyboard navigation and rendering.
type DropdownRow =
  | { kind: 'card'; data: CardSuggestion; didYouMean?: boolean }
  | { kind: 'facet'; category: FacetCategory; value: string };

// ─── Props ───────────────────────────────────────────────────────────────────

interface SearchAutocompleteProps {
  value: string;
  /** Called on every keystroke — parent handles debounce for the grid. */
  onChange: (value: string) => void;
  /** Called when the user selects a card name or history entry, bypassing debounce. */
  onCommit?: (value: string) => void;
  /** When set, invoked on card row pick — includes stable id & artwork (clears input for Riftboundle). */
  onCommitCard?: (card: CardSuggestion) => void;
  /** Only card suggestions: no facets, facet mode, or search history chrome. */
  cardsOnly?: boolean;
  /** Called when the user selects a facet value to apply as a filter. */
  onCommitFacet?: (category: FacetCategory, value: string) => void;
  /** Facet values available for filtering. */
  facets?: FacetsInput;
  placeholder?: string;
  /** Plain text field only — no card suggestions, history, or facet dropdown. */
  plainText?: boolean;
  /** Magnifying glass on the left — plain-text mode only. */
  showSearchIcon?: boolean;
  /** Sets `<input id>` so an external `<label htmlFor>` can target this field */
  inputId?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

// ─── History helpers ──────────────────────────────────────────────────────────

const HISTORY_KEY = 'riftbound_search_history';
const MAX_HISTORY = 12;

function loadHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]).slice(0, MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

function saveHistory(history: string[]) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
  } catch {
    // localStorage may be full or unavailable — ignore
  }
}

function pushHistory(history: string[], term: string): string[] {
  const trimmed = term.trim();
  if (!trimmed) return history;
  const filtered = history.filter((h) => h.toLowerCase() !== trimmed.toLowerCase());
  return [trimmed, ...filtered].slice(0, MAX_HISTORY);
}

// ─── Facet history helpers ────────────────────────────────────────────────────

/** Prefix that marks a history entry as a saved facet filter (not a text search). */
const FACET_HISTORY_PREFIX = '• ';

/** Encode a facet pick as a history string: "• Artist: Alessandro Poli". */
function facetHistoryItem(category: FacetCategory, value: string): string {
  return `${FACET_HISTORY_PREFIX}${FACET_LABEL[category]}: ${value}`;
}

/** Parse a facet history string back to {category, value}, or null for plain text. */
function parseFacetHistory(item: string): { category: FacetCategory; value: string } | null {
  if (!item.startsWith(FACET_HISTORY_PREFIX)) return null;
  const body = item.slice(FACET_HISTORY_PREFIX.length);
  const colonIdx = body.indexOf(': ');
  if (colonIdx === -1) return null;
  const label = body.slice(0, colonIdx);
  const value = body.slice(colonIdx + 2);
  const category = (Object.keys(FACET_LABEL) as FacetCategory[]).find(
    (k) => FACET_LABEL[k] === label,
  );
  if (!category || !value) return null;
  return { category, value };
}

// ─── Facet matching helpers ───────────────────────────────────────────────────

function matchValues(values: string[], query: string, limit: number): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return values.slice(0, limit);
  // Prefer prefix matches first, then substring matches.
  const prefix: string[] = [];
  const substr: string[] = [];
  for (const v of values) {
    const lv = v.toLowerCase();
    if (lv.startsWith(q)) prefix.push(v);
    else if (lv.includes(q)) substr.push(v);
    if (prefix.length >= limit) break;
  }
  return [...prefix, ...substr].slice(0, limit);
}

// ─── Component ────────────────────────────────────────────────────────────────

function PlainSearchInput({
  value,
  onChange,
  onCommit,
  placeholder,
  inputId,
  inputRef: externalInputRef,
  showSearchIcon = false,
}: Pick<
  SearchAutocompleteProps,
  'value' | 'onChange' | 'onCommit' | 'placeholder' | 'inputId' | 'inputRef' | 'showSearchIcon'
>) {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const showClear = Boolean(value);

  const input = (
    <RiftboundInput
      ref={inputRef}
      id={inputId}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onCommit?.(value);
        }
      }}
      placeholder={placeholder ?? 'Search…'}
      autoComplete="off"
      spellCheck={false}
      pr={showSearchIcon ? undefined : showClear ? '40px' : undefined}
    />
  );

  if (showSearchIcon) {
    return (
      <InputGroup
        w="100%"
        startElement={<MdSearch size={16} />}
        endElement={
          showClear ? (
            <RiftboundIconButton
              aria-label="Clear search"
              variant="ghost"
              size="2xs"
              minTouchTarget={false}
              minW="unset"
              color="textMuted"
              onClick={() => onChange('')}
            >
              <MdClose size={14} />
            </RiftboundIconButton>
          ) : undefined
        }
      >
        {input}
      </InputGroup>
    );
  }

  return (
    <Box position="relative" w="100%">
      {input}
      {showClear ? (
        <RiftboundIconButton
          aria-label="Clear search"
          size="xs"
          variant="ghost"
          minTouchTarget={false}
          onClick={() => onChange('')}
          position="absolute"
          right="4px"
          top="50%"
          transform="translateY(-50%)"
          zIndex={2}
        >
          <MdClose size={15} />
        </RiftboundIconButton>
      ) : null}
    </Box>
  );
}

export function SearchAutocomplete(props: SearchAutocompleteProps) {
  if (props.plainText) {
    return (
      <PlainSearchInput
        value={props.value}
        onChange={props.onChange}
        onCommit={props.onCommit}
        placeholder={props.placeholder}
        inputId={props.inputId}
        inputRef={props.inputRef}
        showSearchIcon={props.showSearchIcon}
      />
    );
  }

  return <SearchAutocompleteFaceted {...props} />;
}

function SearchAutocompleteFaceted({
  value,
  onChange,
  onCommit,
  onCommitCard,
  cardsOnly = false,
  onCommitFacet,
  facets,
  placeholder,
  inputId,
  inputRef: externalInputRef,
}: SearchAutocompleteProps) {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownId = useId();

  // ── Suggestions state ──────────────────────────────────────────────────────
  const [cardSuggestions, setCardSuggestions] = useState<CardSuggestion[]>([]);
  const [didYouMean, setDidYouMean] = useState<CardSuggestion | null>(null);
  const [loadingCards, setLoadingCards] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // ── Facet mode (triggered by `/`) ──────────────────────────────────────────
  /** When non-null, the user pressed `/` and we are filtering this category only. */
  const [activeTab, setActiveTab] = useState<TabId>('all');
  /** True while in facet-mode (entered via `/`). Adds visual hint + different parsing. */
  const [facetMode, setFacetMode] = useState(false);

  // ── History state ──────────────────────────────────────────────────────────
  const [history, setHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // ── Abort / debounce refs ──────────────────────────────────────────────────
  const abortRef = useRef<AbortController | null>(null);
  const suggestTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Load history on mount
  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  // ── Derived: query without the leading `/` in facet mode ───────────────────
  const effectiveQuery = useMemo(() => {
    if (facetMode && value.startsWith('/')) return value.slice(1);
    return value;
  }, [facetMode, value]);

  // ── Fetch card suggestions ─────────────────────────────────────────────────
  const fetchCardSuggestions = useCallback(
    async (query: string) => {
      if (!query || query.length < 1) {
        setCardSuggestions([]);
        setDidYouMean(null);
        return;
      }

      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setLoadingCards(true);
      try {
        const base = getApiBaseUrl();
        const res = await fetch(
          `${base}/api/suggest?q=${encodeURIComponent(query)}`,
          { signal: abortRef.current.signal },
        );
        if (!res.ok) throw new Error('suggest failed');
        const data: { suggestions: CardSuggestion[]; didYouMean?: CardSuggestion } =
          await res.json();
        setCardSuggestions(data.suggestions ?? []);
        setDidYouMean(data.didYouMean?.id ? data.didYouMean : null);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setCardSuggestions([]);
        setDidYouMean(null);
      } finally {
        setLoadingCards(false);
      }
    },
    [],
  );

  // Cards-only UX: facet mode adds noise for pickers like Riftboundle.
  useEffect(() => {
    if (!cardsOnly) return;
    if (facetMode) {
      setFacetMode(false);
    }
    if (activeTab !== 'cards') {
      setActiveTab('cards');
    }
  }, [cardsOnly, facetMode, activeTab]);

  // ── Available facet lists ──────────────────────────────────────────────────
  const facetLists = useMemo(
    () => ({
      keyword: facets?.keywords ?? [],
      cardType: facets?.cardTypes ?? [],
      supertype: facets?.supertypes ?? [],
      region: facets?.regions ?? [],
      subtype: facets?.subtypes ?? [],
      rarity: facets?.rarities ?? [],
      domain: facets?.domains ?? DOMAINS,
      artist: facets?.artists ?? [],
    }),
    [facets],
  );

  // ── Build dropdown rows based on active tab + query ────────────────────────
  const rows = useMemo<DropdownRow[]>(() => {
    const cardRows = (suggestions: CardSuggestion[], limit?: number): DropdownRow[] => {
      const capped = limit != null ? suggestions.slice(0, limit) : suggestions;
      if (!didYouMean) {
        return capped.map((c) => ({ kind: 'card' as const, data: c }));
      }
      // Pin the correction under a "Did you mean" header; keep other hits below.
      const rest = capped.filter((c) => c.id !== didYouMean.id);
      const budget = limit != null ? Math.max(0, limit - 1) : rest.length;
      return [
        { kind: 'card' as const, data: didYouMean, didYouMean: true },
        ...rest.slice(0, budget).map((c) => ({ kind: 'card' as const, data: c })),
      ];
    };

    if (!showHistory && cardsOnly) {
      return cardRows(cardSuggestions);
    }
    const tab = activeTab;

    // Helper: build facet rows for a given category.
    const facetRows = (category: FacetCategory, limit: number): DropdownRow[] =>
      matchValues(facetLists[category], effectiveQuery.trim(), limit).map((v) => ({
        kind: 'facet',
        category,
        value: v,
      }));

    if (tab === 'cards') {
      return cardRows(cardSuggestions);
    }

    if (tab !== 'all') {
      return facetRows(tab, SINGLE_TAB_LIMIT);
    }

    // ── "All" tab: mix cards + a few of each facet category ──────────────────
    const allRows: DropdownRow[] = [];
    if ((cardSuggestions.length > 0 || didYouMean) && !facetMode) {
      allRows.push(...cardRows(cardSuggestions, ALL_TAB_CARDS_LIMIT));
    }
    (
      ['keyword', 'cardType', 'supertype', 'subtype', 'region', 'domain', 'rarity', 'artist'] as FacetCategory[]
    ).forEach((category) => {
      allRows.push(...facetRows(category, ALL_TAB_FACET_LIMIT));
    });
    return allRows;
  }, [activeTab, effectiveQuery, cardSuggestions, didYouMean, facetLists, facetMode, cardsOnly, showHistory]);

  // Reset activeIndex when the list shape changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [rows]);

  // ── Input change ───────────────────────────────────────────────────────────
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      onChange(val);

      // Enter facet mode if user types `/` as the first character.
      if (!cardsOnly && !facetMode && val === '/') {
        setFacetMode(true);
        setActiveTab('all');
        setDropdownOpen(true);
        setShowHistory(false);
        return;
      }

      // Exit facet mode if the leading `/` is removed.
      if (facetMode && !val.startsWith('/')) {
        setFacetMode(false);
      }

      setShowHistory(false);

      if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current);

      if (!val) {
        setCardSuggestions([]);
        setDidYouMean(null);
        setLoadingCards(false);
        setDropdownOpen(false);
        abortRef.current?.abort();
        return;
      }

      setDropdownOpen(true);

      // In facet mode, only fetch card suggestions when on the "cards" or "all" tab
      // and the query (without leading slash) is non-empty.
      const queryForCards = val.startsWith('/') ? val.slice(1) : val;
      // Match /api/suggest: skip 1-char queries (server returns [] and avoids Neon).
      const shouldFetchCards =
        (cardsOnly && val.length >= 2) ||
        (!facetMode && val.length >= 2) ||
        (facetMode && (activeTab === 'all' || activeTab === 'cards') && queryForCards.length >= 2);

      if (shouldFetchCards) {
        // 280ms cuts Neon keystroke churn vs 120ms while still feeling snappy.
        suggestTimeoutRef.current = setTimeout(
          () => fetchCardSuggestions(queryForCards),
          280,
        );
      } else {
        setCardSuggestions([]);
        setDidYouMean(null);
      }
    },
    [onChange, fetchCardSuggestions, facetMode, activeTab, cardsOnly],
  );

  // ── Commit selection ───────────────────────────────────────────────────────
  const commitCard = useCallback(
    (term: string, suggestion?: CardSuggestion) => {
      if (onCommitCard && suggestion) {
        onCommitCard(suggestion);
        onChange('');
        setDropdownOpen(false);
        setShowHistory(false);
        setActiveIndex(-1);
        setFacetMode(false);
        return;
      }

      onChange(term);
      onCommit?.(term);
      const updated = pushHistory(history, term);
      setHistory(updated);
      saveHistory(updated);
      setDropdownOpen(false);
      setShowHistory(false);
      setActiveIndex(-1);
      setFacetMode(false);
    },
    [onChange, onCommit, onCommitCard, history],
  );

  const commitFacet = useCallback(
    (category: FacetCategory, val: string) => {
      onCommitFacet?.(category, val);
      // Save the facet pick to history so it appears in "Recent searches".
      const updated = pushHistory(history, facetHistoryItem(category, val));
      setHistory(updated);
      saveHistory(updated);
      // Clear the input and exit facet mode so the user can keep adding filters.
      onChange('');
      setCardSuggestions([]);
      setDidYouMean(null);
      setActiveIndex(-1);
      setFacetMode(false);
      setActiveTab('all');
      setDropdownOpen(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [onCommitFacet, onChange, inputRef, history],
  );

  /** Replay a history entry: restores a facet filter or sets a text search term. */
  const commitHistoryItem = useCallback(
    (term: string) => {
      const facet = parseFacetHistory(term);
      if (facet) {
        onCommitFacet?.(facet.category, facet.value);
        setDropdownOpen(false);
        setShowHistory(false);
        setActiveIndex(-1);
      } else {
        commitCard(term);
      }
    },
    [onCommitFacet, commitCard],
  );

  const commitRow = useCallback(
    (row: DropdownRow) => {
      if (row.kind === 'card') {
        const raw = row.data.name;
        const term = stripTrailingPrintSuffix(raw).trim() || raw.trim();
        commitCard(term, row.data);
      } else commitFacet(row.category, row.value);
    },
    [commitCard, commitFacet],
  );

  // ── Keyboard navigation ────────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // `/` shortcut to enter facet mode (only when input is empty / just `/`).
      if (!cardsOnly && e.key === '/' && !facetMode && value === '') {
        e.preventDefault();
        setFacetMode(true);
        setActiveTab('all');
        setDropdownOpen(true);
        setShowHistory(false);
        onChange('/');
        return;
      }

      // Backspace on a bare `/` exits facet mode.
      if (!cardsOnly && e.key === 'Backspace' && facetMode && value === '/') {
        e.preventDefault();
        setFacetMode(false);
        onChange('');
        setDropdownOpen(false);
        return;
      }

      const listLen = showHistory ? history.length : rows.length;

      switch (e.key) {
        case 'ArrowDown':
          if (dropdownOpen) {
            e.preventDefault();
            setActiveIndex((i) => (i < listLen - 1 ? i + 1 : 0));
          }
          break;
        case 'ArrowUp':
          if (dropdownOpen) {
            e.preventDefault();
            setActiveIndex((i) => (i > 0 ? i - 1 : listLen - 1));
          }
          break;
        case 'Tab':
          // Tab cycles category tabs while the dropdown is open.
          if (!cardsOnly && dropdownOpen && !showHistory) {
            e.preventDefault();
            const idx = TABS.findIndex((t) => t.id === activeTab);
            const next = TABS[(idx + (e.shiftKey ? -1 : 1) + TABS.length) % TABS.length];
            setActiveTab(next.id);
            setActiveIndex(-1);
          }
          break;
        case 'Enter':
          if (showHistory && activeIndex >= 0) {
            e.preventDefault();
            commitHistoryItem(history[activeIndex]);
          } else if (dropdownOpen && activeIndex >= 0) {
            e.preventDefault();
            commitRow(rows[activeIndex]);
          } else if (!facetMode && !cardsOnly) {
            // Commit current input value as free-text search
            onCommit?.(value);
            const updated = pushHistory(history, value);
            setHistory(updated);
            saveHistory(updated);
            setDropdownOpen(false);
            setShowHistory(false);
          }
          break;
        case 'Escape':
          if (facetMode) {
            setFacetMode(false);
            onChange('');
          }
          setDropdownOpen(false);
          setShowHistory(false);
          setActiveIndex(-1);
          break;
        default:
          break;
      }
    },
    [
      facetMode,
      value,
      onChange,
      showHistory,
      history,
      rows,
      dropdownOpen,
      activeTab,
      activeIndex,
      commitHistoryItem,
      commitRow,
      onCommit,
      cardsOnly,
    ],
  );

  // ── Close on click-outside ─────────────────────────────────────────────────
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      setDropdownOpen(false);
      setShowHistory(false);
      setActiveIndex(-1);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current);
    };
  }, []);

  // ── Clear ──────────────────────────────────────────────────────────────────
  const handleClear = () => {
    onChange('');
    onCommit?.('');
    setCardSuggestions([]);
    setDidYouMean(null);
    setDropdownOpen(false);
    setShowHistory(false);
    setFacetMode(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // ── History toggle ─────────────────────────────────────────────────────────
  const toggleHistory = () => {
    setShowHistory((v) => {
      if (!v) setDropdownOpen(true);
      return !v;
    });
    setActiveIndex(-1);
  };

  const showClear = Boolean(value);
  const effectivePlaceholder =
    placeholder ??
    (cardsOnly
      ? 'Search for a card to guess…'
      : facetMode
        ? 'Filter by keyword, type, artist…'
        : 'Search cards or / for filters');

  return (
    <Box ref={containerRef} position="relative" w="100%">
      {/* ── Input row ─────────────────────────────────────────────────────── */}
      <HStack gap={1} align="stretch">
        <Box position="relative" flex={1}>
          <RiftboundInput
            ref={inputRef}
            id={inputId}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (cardsOnly) return;
              if (!value && history.length > 0) {
                setShowHistory(true);
                setDropdownOpen(true);
              }
            }}
            placeholder={effectivePlaceholder}
            autoComplete="off"
            spellCheck={false}
            pr={
              cardsOnly
                ? showClear
                  ? '40px'
                  : '12px'
                : showClear
                  ? '72px'
                  : '40px'
            }
            borderColor={facetMode ? 'accent.500' : undefined}
            aria-autocomplete="list"
            aria-controls={dropdownOpen ? dropdownId : undefined}
            aria-expanded={dropdownOpen}
          />

          {!cardsOnly && (
          <RiftboundIconButton
            aria-label="Recent searches"
            size="xs"
            variant="ghost"
            minTouchTarget={false}
            onClick={toggleHistory}
            position="absolute"
            right={showClear ? '34px' : '4px'}
            top="50%"
            transform="translateY(-50%)"
            zIndex={2}
            color={showHistory ? 'accent.500' : 'textMuted'}
          >
            <MdHistory size={15} />
          </RiftboundIconButton>
          )}

          {/* Clear button */}
          {showClear && (
            <RiftboundIconButton
              aria-label="Clear search"
              size="xs"
              variant="ghost"
              minTouchTarget={false}
              onClick={handleClear}
              position="absolute"
              right="4px"
              top="50%"
              transform="translateY(-50%)"
              zIndex={2}
            >
              <MdClose size={15} />
            </RiftboundIconButton>
          )}
        </Box>

      </HStack>

      {/* ── Dropdown ──────────────────────────────────────────────────────── */}
      {dropdownOpen && (
        <Box
          id={dropdownId}
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={1000}
          bg={dropdownBg}
          border="hairline"
          borderColor="border"
          borderRadius="md"
          boxShadow={raisedShadow}
          maxH="420px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          {/* Facet-mode hint */}
          {!cardsOnly && facetMode && !showHistory && (
            <HStack
              px={3}
              py={1.5}
              gap={2}
              borderBottom="1px solid"
              borderColor="border"
              flexShrink={0}
            >
              <Badge size="sm" colorPalette="yellow" variant="subtle">
                Filter mode
              </Badge>
              <Text fontSize="xs" color="textMuted">
                Type to filter, Esc to exit
              </Text>
            </HStack>
          )}

          {/* Category tabs (hidden in history mode) */}
          {!showHistory && !cardsOnly && (
            <HStack
              px={2}
              py={1.5}
              gap={1}
              borderBottom="1px solid"
              borderColor="border"
              flexShrink={0}
              overflowX="auto"
              css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
            >
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <RiftboundButton
                    key={tab.id}
                    size="sm"
                    variant={isActive ? 'solid' : 'ghost'}
                    bg={isActive ? 'selected.bg' : undefined}
                    color={isActive ? 'selected.fg' : 'textMuted'}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setActiveIndex(-1);
                    }}
                    px={2}
                    minH="unset"
                    h="auto"
                    py={1}
                    fontSize="2xs"
                    flexShrink={0}
                  >
                    {tab.label}
                  </RiftboundButton>
                );
              })}
            </HStack>
          )}

          {/* Suggestions body */}
          {!showHistory && (
            <Box overflowY="auto" maxH="360px">
              {loadingCards && (activeTab === 'all' || activeTab === 'cards') && rows.length === 0 ? (
                <Flex justify="center" p={4}>
                  <Spinner size="sm" />
                </Flex>
              ) : rows.length > 0 ? (
                rows.map((row, idx) =>
                  row.kind === 'card' ? (
                    <Box key={`card-${row.data.id}-${row.didYouMean ? 'dym' : 'hit'}`}>
                      {row.didYouMean && (
                        <SearchSuggestionSectionLabel>Did you mean</SearchSuggestionSectionLabel>
                      )}
                      <CardSearchSuggestionRow
                        suggestion={row.data}
                        isActive={idx === activeIndex}
                        onPointerEnter={() => setActiveIndex(idx)}
                        onClick={() => commitRow(row)}
                      />
                    </Box>
                  ) : (
                    <FacetRow
                      key={`${row.category}-${row.value}`}
                      category={row.category}
                      value={row.value}
                      isActive={idx === activeIndex}
                      onPointerEnter={() => setActiveIndex(idx)}
                      onClick={() => commitRow(row)}
                    />
                  ),
                )
              ) : (
                <Box px={3} py={4} textAlign="center">
                  <Text fontSize="sm" color="textMuted">
                    {effectiveQuery.trim()
                      ? 'No matches'
                      : facetMode
                        ? 'Type to filter or pick a category above'
                        : 'Start typing to search'}
                  </Text>
                </Box>
              )}
            </Box>
          )}

          {/* History panel */}
          {showHistory && (
            <>
              <HStack
                px={3}
                py={2}
                borderBottom="1px solid"
                borderColor="border"
                justify="space-between"
                flexShrink={0}
              >
                <Text fontSize="xs" fontWeight="semibold" color="textMuted" userSelect="none">
                  Recent searches
                </Text>
                {history.length > 0 && (
                  <RiftboundButton
                    treatment="ghost"
                    size="sm"
                    color="textMuted"
                    onClick={() => {
                      setHistory([]);
                      saveHistory([]);
                    }}
                  >
                    Clear all
                  </RiftboundButton>
                )}
              </HStack>

              <Box overflowY="auto" maxH="320px">
                {history.length > 0 ? (
                  history.map((term, idx) => {
                    const isFacet = Boolean(parseFacetHistory(term));
                    return (
                    <HStack
                      key={idx}
                      px={3}
                      py={2}
                      gap={2}
                      cursor="pointer"
                      bg={idx === activeIndex ? 'surface' : 'transparent'}
                      _hover={{ bg: 'surface' }}
                      onPointerEnter={() => setActiveIndex(idx)}
                      onClick={() => commitHistoryItem(term)}
                      borderBottom="1px solid"
                      borderColor="border"
                      justify="space-between"
                    >
                      <HStack gap={2} flex={1} minW={0}>
                        <Box color="textMuted" flexShrink={0}>
                          {isFacet ? <MdFilterList size={14} /> : <MdHistory size={14} />}
                        </Box>
                        <Text
                          fontSize="sm"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {term}
                        </Text>
                      </HStack>
                      <Box
                        as="button"
                        color="textMuted"
                        _hover={{ color: 'text' }}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          const updated = history.filter((_, i) => i !== idx);
                          setHistory(updated);
                          saveHistory(updated);
                        }}
                        display="flex"
                        alignItems="center"
                        flexShrink={0}
                      >
                        <MdClose size={14} />
                      </Box>
                    </HStack>
                  );})
                ) : (
                  <Box px={3} py={4} textAlign="center">
                    <Text fontSize="sm" color="textMuted">
                      No recent searches
                    </Text>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Facet Row ────────────────────────────────────────────────────────────────

interface FacetRowProps {
  category: FacetCategory;
  value: string;
  isActive: boolean;
  onPointerEnter: () => void;
  onClick: () => void;
}

function facetIcon(category: FacetCategory, value: string) {
  if (category === 'rarity') return getRarityIcon(value, 18);
  if (category === 'domain') return <DomainIcon value={value} size={18} />;
  return null;
}

function FacetRow({ category, value, isActive, onPointerEnter, onClick }: FacetRowProps) {
  const icon = facetIcon(category, value);

  return (
    <HStack
      px={3}
      py={2}
      gap={3}
      cursor="pointer"
      bg={isActive ? 'surface' : 'transparent'}
      _hover={{ bg: 'surface' }}
      onPointerEnter={onPointerEnter}
      onClick={onClick}
      borderBottom="1px solid"
      borderColor="border"
      align="center"
      justify="space-between"
    >
      <HStack gap={2} flex={1} minW={0} align="center">
        {icon && (
          <Box flexShrink={0} display="flex" alignItems="center" justifyContent="center" w="18px" h="18px">
            {icon}
          </Box>
        )}
        <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {value}
        </Text>
      </HStack>
      <Badge size="sm" variant="subtle" colorPalette="purple" flexShrink={0}>
        {FACET_LABEL[category]}
      </Badge>
    </HStack>
  );
}
